import { useState, useEffect, useCallback } from "react";
import { Bell, Loader2, Send, Info } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

export default function NotificationSettings({ restaurantId, restaurantName }) {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const p = await api.listNotificationPreferences(restaurantId);
      setPrefs(p || []);
    } catch (err) {
      toast.error(err.message || "Error al cargar preferencias");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (channel, enabled) => {
    setToggling(channel);
    // Optimistic update
    setPrefs(ps => ps.map(p => p.channel === channel ? { ...p, enabled } : p));
    try {
      await api.updateNotificationPreference(restaurantId, channel, enabled);
      toast.success(enabled ? "Notificaciones activadas" : "Notificaciones desactivadas");
    } catch (err) {
      toast.error(err.message || "Error al guardar");
      setPrefs(ps => ps.map(p => p.channel === channel ? { ...p, enabled: !enabled } : p));
    } finally { setToggling(null); }
  };

  const sendNow = async (channel) => {
    setSending(channel);
    try {
      const res = await api.sendNotificationNow(restaurantId, channel);
      toast.success(`Email enviado a ${res.sent_to}`);
    } catch (err) {
      toast.error(err.message || "No se pudo enviar");
    } finally { setSending(null); }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="notification-settings">
      <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
        <Info className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" />
        <div className="text-sm text-sky-900 leading-relaxed">
          Estos ajustes aplican solo a <strong>{restaurantName || "este restaurante"}</strong>.
          Los emails de seguridad (OTP de borrado, invitaciones al equipo) se envían siempre y no son desactivables.
        </div>
      </div>

      {prefs.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-sm text-muted-foreground">No hay canales disponibles.</CardContent></Card>
      ) : prefs.map(pref => (
        <Card key={pref.channel} data-testid={`notif-pref-${pref.channel}`}>
          <CardContent className="p-4 flex items-start gap-4">
            <div className={`shrink-0 h-10 w-10 rounded-xl flex items-center justify-center ${pref.enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
              <Bell className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground">{pref.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{pref.description}</div>
              {pref.enabled && (
                <button
                  onClick={() => sendNow(pref.channel)}
                  disabled={sending === pref.channel}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:opacity-50"
                  data-testid={`notif-send-now-${pref.channel}`}
                >
                  {sending === pref.channel ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Enviarme una prueba ahora
                </button>
              )}
            </div>
            <Switch
              checked={pref.enabled}
              disabled={toggling === pref.channel}
              onCheckedChange={(v) => toggle(pref.channel, v)}
              data-testid={`notif-toggle-${pref.channel}`}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
